import { cva, type VariantProps } from 'class-variance-authority';
import { ReactNode } from 'react';
import { Checkbox, CheckboxProps } from 'react-aria-components';
import { cn } from './utils';

const agCheckboxVariants = cva(
  'rounded-xs border bg-white shadow-xs flex items-center justify-center transition-colors',
  {
    variants: {
      size: { sm: 'w-4 h-4', md: 'w-4.5 h-4.5', lg: 'w-5 h-5' },
      variant: {
        default: 'border-neutral-5 group-data-selected:bg-brand-6 group-data-selected:border-brand-6',
        error: 'border-red-text group-data-selected:bg-red-text group-data-selected:border-red-text',
      },
      isDisabled: { true: 'cursor-not-allowed', false: '' },
    },
    defaultVariants: { size: 'md', variant: 'default', isDisabled: false },
  }
);

const agCheckmarkVariants = cva('text-white opacity-0 group-data-selected:opacity-100 transition-opacity', {
  variants: { size: { sm: 'w-2.5 h-2.5', md: 'w-3 h-3', lg: 'w-3.5 h-3.5' } },
  defaultVariants: { size: 'md' },
});

const agLabelVariants = cva('text-neutral-text', {
  variants: { size: { sm: 'text-13', md: 'text-14', lg: 'text-16' } },
  defaultVariants: { size: 'md' },
});

type AgCheckboxVariantProps = VariantProps<typeof agCheckboxVariants>;

export interface AgCheckboxProps extends Omit<CheckboxProps, 'children'> {
  /** Label text displayed next to the checkbox */
  label?: ReactNode;
  /** Show red asterisk for required fields */
  isRequired?: boolean;
  /** Size variant */
  size?: AgCheckboxVariantProps['size'];
  /** Whether the checkbox is in an error state */
  isInvalid?: boolean;
  /** Additional class for the container */
  className?: string;
}

export function AgCheckbox({
  label,
  isRequired,
  size = 'md',
  isInvalid,
  isDisabled,
  className,
  ...props
}: AgCheckboxProps) {
  return (
    <Checkbox
      isDisabled={isDisabled}
      className={cn(
        'group relative flex items-center gap-2 cursor-pointer data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
        className
      )}
      {...props}
    >
      <div className={cn(agCheckboxVariants({ size, variant: isInvalid ? 'error' : 'default', isDisabled }), '')}>
        <svg
          className={cn(agCheckmarkVariants({ size }))}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={4}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      {label && (
        <span className={cn(agLabelVariants({ size }))}>
          {label}
          {isRequired && <span className="text-red-text ml-2">*</span>}
        </span>
      )}
    </Checkbox>
  );
}

AgCheckbox.displayName = 'AgCheckbox';
